import { ConsignmentApiResponse, ConsignmentDetails, ConsignmentHistoryItem } from './redux/ConsignmentDetailsSlice.type';

export class ConsignmentDetailsMapper {
  
  /**
   * Formats date as Today, Tomorrow, or actual date with timezone conversion
   */
  static formatRelativeDate(dateString: string, timezone: string = 'Asia/Dubai'): string {
    // Get current date in the specified timezone
    const now = new Date();
    const todayInTimezone = new Date(now.toLocaleString('en-US', { timeZone: timezone }));
    const tomorrowInTimezone = new Date(todayInTimezone);
    tomorrowInTimezone.setDate(todayInTimezone.getDate() + 1);

    // Parse delivery date (assuming it's already in the correct format like "2025-09-27")
    const deliveryDate = new Date(dateString);

    // Get date strings for comparison (YYYY-MM-DD format)
    const todayStr = todayInTimezone.toISOString().split('T')[0];
    const tomorrowStr = tomorrowInTimezone.toISOString().split('T')[0];
    const deliveryStr = deliveryDate.toISOString().split('T')[0];

    if (deliveryStr === todayStr) {
      return 'Today';
    } else if (deliveryStr === tomorrowStr) {
      return 'Tomorrow';
    } else {
      return deliveryDate.toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
        year: 'numeric'
      });
    }
  }

  /**
   * Converts UTC timestamp to local timezone and formats time
   */
  static formatTimeWithTimezone(utcTimeString: string, timezone: string = 'Asia/Dubai'): string {
    // Ensure we're working with a proper UTC date
    const properUtcDate = new Date(utcTimeString + (utcTimeString.includes('Z') ? '' : 'Z'));
    
    return properUtcDate.toLocaleTimeString('en-US', {
      timeZone: timezone,
      hour: 'numeric',
      minute: '2-digit',
      hour12: true
    });
  }
  
  /**
   * Maps API response to UI format
   */
  static mapApiResponseToConsignmentDetails(apiResponse: any): ConsignmentDetails {
    // Handle the actual API response structure
    const order = apiResponse.order || apiResponse;
    const products = apiResponse.products || [];
    const statuses = apiResponse.statuses || [];
    const route = apiResponse.route || {};
    
    // Get timezone from API response, default to Asia/Dubai for UAE
    const timezone = order?.metadata?.timeZone || 'Asia/Dubai';
    
    // Build timeline from statuses - sorted by date (with safety check and timezone conversion)
    const consignmentHistory: ConsignmentHistoryItem[] = Array.isArray(statuses) 
      ? statuses
          .sort((a, b) => new Date(a.statusDate).getTime() - new Date(b.statusDate).getTime())
          .map((status) => {
            // Convert UTC timestamp to local timezone
            const utcDate = new Date(status.statusDate);
            
            // Ensure we're working with a proper UTC date
            const properUtcDate = new Date(status.statusDate + (status.statusDate.includes('Z') ? '' : 'Z'));
            
            const localTimestamp = properUtcDate.toLocaleString('en-US', {
              timeZone: timezone,
              year: 'numeric',
              month: 'short',
              day: 'numeric',
              hour: 'numeric',
              minute: '2-digit',
              hour12: true
            });
            
            
            return {
              status: status.statusName,
              timestamp: localTimestamp,
              isCompleted: false // Required by interface but not used
            };
          })
      : [];
    
    // Build customer address (with safety checks)
    const customerAddr = order?.deliveryInformation?.customerContactInfo?.address || {};
    const customerAddress = [
      customerAddr.building,
      customerAddr.apartment,
      customerAddr.streetName,
      customerAddr.area,
      customerAddr.city,
      customerAddr.countryName
    ].filter(Boolean).join(', ');
    
    // Build hub address (with safety checks)
    const hubAddress = [
      order?.posInformation?.contactInfo?.address?.area,
      order?.posInformation?.contactInfo?.address?.city
    ].filter(Boolean).join(', ');
    
    return {
      consignmentId: order?.consignmentCode || 'N/A',
      status: order?.metadata?.status?.displayDeliveryStatus || 'Unknown',
      liveTracking: order?.deliveryInformation?.carrierTrackingUrl,
      viewInvoice: order?.metadata?.invoiceFileLink,
      
      orderDetail: {
        orderNo: order?.orderCode || 'N/A',
        deliveryDate: order?.deliveryInformation?.deliverySlot?.deliveryDate 
          ? ConsignmentDetailsMapper.formatRelativeDate(order.deliveryInformation.deliverySlot.deliveryDate, timezone)
          : 'N/A',
        deliverySlot: order?.deliveryInformation?.deliverySlot?.deliverySlotTime || 'N/A',
        deliveryEndTime: order?.deliveryInformation?.deliverySlot?.endTime 
          ? ConsignmentDetailsMapper.formatTimeWithTimezone(order.deliveryInformation.deliverySlot.endTime, timezone)
          : 'N/A',
        deliveryProposition: order?.metadata?.deliveryType || 'N/A',
        deliveryArea: order?.metadata?.deliveryZone || 'N/A',
        paymentMode: order?.metadata?.paymentType || 'N/A',
        routeNo: order?.metadata?.routeName || 'N/A',
        driver: order?.deliveryInformation?.driver?.name || 'N/A',
        vehicle: order?.deliveryInformation?.driver?.vehicleType || 'N/A',
        paymentStatus: order?.metadata?.paymentMethod || 'N/A',
        totalAmount: order?.metadata?.totalAmount || '0',
        currency: order?.metadata?.currency || 'AED',
        category: order?.metadata?.category || 'N/A',
        activity: order?.metadata?.activity || 'N/A',
        deliveryCost: order?.deliveryInformation?.deliveryCost || '0',
        totalWeight: order?.deliveryInformation?.totalWeight || '0'
      },
      
      customerDetails: {
        name: order?.deliveryInformation?.customerContactInfo?.name || 'N/A',
        verified: customerAddr?.status === 'VERIFIED',
        address: customerAddress || 'N/A',
        area: customerAddr?.area || 'N/A',
        phone: order?.deliveryInformation?.customerContactInfo?.phone || 'N/A',
        email: order?.deliveryInformation?.customerContactInfo?.email || 'N/A'
      },
      
      hubDetails: {
        name: order?.posInformation?.storeName || 'N/A',
        address: hubAddress || 'N/A',
        storeCode: order?.posInformation?.store || 'N/A'
      },
      
      driverDetails: {
        primaryDriver: {
          name: order?.deliveryInformation?.driver?.name || 'N/A',
          phone: order?.deliveryInformation?.driver?.phone || 'N/A',
          vehicleType: order?.deliveryInformation?.driver?.vehicleType || 'N/A',
          vendor: order?.deliveryInformation?.driver?.vendor || 'N/A'
        }
      },
      
      products: Array.isArray(products) ? products.map((product: any) => ({
        sku: product?.sku || 'N/A',
        name: product?.name?.en || product?.name || 'N/A',
        quantity: product?.quantity || '0',
        unitPrice: product?.unitPrice || '0',
        imgUrl: product?.imgUrl || '',
        uom: product?.uom || 'N/A'
      })) : [],
      
      consignmentHistory
    };
  }
  
  /**
   * Maps multiple API responses to UI format
   */
  static mapApiResponseArrayToConsignmentDetails(apiResponses: ConsignmentApiResponse[]): ConsignmentDetails[] {
    return apiResponses.map(apiResponse => this.mapApiResponseToConsignmentDetails(apiResponse));
  }
  
  
  /**
   * Validate API response structure
   */
  static validateApiResponse(apiResponse: any): apiResponse is ConsignmentApiResponse {
    return (
      apiResponse &&
      apiResponse.order &&
      apiResponse.order.consignmentCode &&
      apiResponse.order.deliveryInformation &&
      apiResponse.products &&
      Array.isArray(apiResponse.products) &&
      apiResponse.statuses &&
      Array.isArray(apiResponse.statuses)
    );
  }
}
